/**
 * App-side glue for the unified `instavcMessenger.ts`.
 *
 * `instavcMessenger.ts` is the portable, framework-free SDK (one file, no app
 * imports). This module is the Web_Client's thin binding to it: it sources the
 * Firebase / VAPID / SDK-API config from `VITE_*` env, reuses the app's shared
 * Phoenix socket (so the SDK multiplexes onto the ONE connection the app
 * already opens for meetings + presence — never a second WebSocket), and reads
 * the current auth token from the session store.
 *
 * Push works without a socket, so `registerWebPush()` / `unregisterWebPush()`
 * spin up a push-only SDK instance and don't touch the socket. The same is true
 * of the REST-only surfaces — contacts identity and call history — which run on
 * a shared socket-free instance. A full SDK instance (chat + presence) is
 * available via `getInstavcSdk()` for callers that want to drive chat/presence
 * through the SDK directly.
 */

import {
  InstavcSdk,
  PushModule,
  type PushOpen,
  type FirebaseWebConfig,
  type SelfContact,
  type RegisterSelfInput,
  type CallHistoryPage,
  type BlockedContact,
  type PrivacySettings,
  type ChatPreferences,
  type StarredPage,
  type Broadcast as SdkBroadcast,
  type BroadcastRecipient,
  type BroadcastSendResult,
  type BroadcastMessageReceipts,
  type MessageStatus,
} from './instavcMessenger'
import { getPhoenixSocket } from '@/lib/phoenix-socket'
import { getDeviceId } from '@/lib/device-id'
import { ensureFreshAccessToken } from '@/lib/auth-refresh'

const env = import.meta.env

const SDK_API_URL: string = env.VITE_SDK_API_URL || env.VITE_API_URL || ''

/** Firebase web config (public — safe in the bundle). Env-overridable; the
 *  `inlynk-agentic-dev` project values are the fallback so a build without env still
 *  works. Keep `public/firebase-messaging-sw.js` in sync with these defaults. */
const firebase: FirebaseWebConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY || 'AIzaSyAFMWccQ0sN55o1UvfunEkKKwgRPuMOLEU',
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || 'inlynk-agentic-dev.firebaseapp.com',
  projectId: env.VITE_FIREBASE_PROJECT_ID || 'inlynk-agentic-dev',
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || 'inlynk-agentic-dev.firebasestorage.app',
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || '501592645035',
  appId: env.VITE_FIREBASE_APP_ID || '1:501592645035:web:8dbd467211a9aa46fbc6ee',
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID || 'G-NZNQSKM4MG',
}

/** Web Push VAPID **public** key (private half stays in Firebase). */
const vapidKey: string =
  env.VITE_FCM_VAPID_KEY ||
  'BFWjYF22FKLGp0KemJDjjdQfegCHDUI1mzdtuz3T0ESxIy8iWpcXcIe2XAFFepT9j2FwF2iDGIjv1LiVvUaSa8s'

/** Read the current app Bearer token from the session store (empty if none). */
function authToken(): string {
  try {
    // Primary key is `instavc_auth_session`; the legacy `inmeet_auth_session`
    // key is read as a fallback so a host app that hasn't migrated its stored
    // session key keeps working.
    const raw =
      localStorage.getItem('instavc_auth_session') ||
      sessionStorage.getItem('instavc_auth_session') ||
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const s = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }).session
    if (s?.expiresAt && s.expiresAt <= Date.now()) return ''
    return s?.accessToken || ''
  } catch {
    return ''
  }
}

/** The app Bearer supplier fed to the SDK as `getAppToken`; the SDK exchanges it
 *  internally for an SDK session token (cached) for REST/push.
 *
 *  Renews before giving up. `authToken()` returns '' once the stored token is
 *  past its expiry, and the SDK treats an empty bearer as a hard error
 *  ("getAppToken returned an empty token") — so chat, presence and push would
 *  fail outright rather than wait the moment it takes to refresh.
 *
 *  That window barely existed while the app token lived 24h. At 15 minutes it
 *  opens whenever the background refresher is late: a backgrounded tab, a
 *  slept machine, or a 503 the refresher is still backing off from. */
async function phoenixToken(): Promise<string> {
  const current = authToken()
  if (current) return current
  return ensureFreshAccessToken()
}

// ─── Push (no socket needed) ─────────────────────────────────────────────────

let pushSdk: InstavcSdk | null = null

function pushInstance(userId: string): InstavcSdk {
  if (pushSdk) return pushSdk
  pushSdk = new InstavcSdk({
    userId,
    sdkApiUrl: SDK_API_URL,
    getAppToken: phoenixToken,
    deviceId: getDeviceId(),
    firebase,
    vapidKey,
    logger: (level, msg, data) => {
      if (level === 'error' || level === 'warn') console.warn(`[instavcMessenger] ${msg}`, data ?? '')
    },
  })
  return pushSdk
}

/** Register this browser for FCM push (idempotent, best-effort, never throws). */
export async function registerWebPush(userId: string): Promise<boolean> {
  return pushInstance(userId).push.register()
}

/** Delete this browser's FCM token locally + on the server (call on logout). */
export async function unregisterWebPush(userId: string): Promise<void> {
  await pushInstance(userId).push.unregister()
  pushSdk = null
}

/** Click-only push module. Deliberately NOT the cached `pushSdk`: a click can
 *  land before auth rehydrates, and seeding `pushInstance` with a placeholder
 *  userId would pin the registration instance to the wrong user (first call
 *  wins). Reading SW messages needs no identity or token, so this is free. */
let clickPush: PushModule | null = null

/**
 * Subscribe to notification CLICKS forwarded by the service worker. Returns an
 * unsubscribe function.
 *
 * Only covers clicks that reach an ALREADY-OPEN tab. A click with no tab open
 * launches one and arrives as `?c=` on the URL instead — PresencePage handles
 * both paths.
 */
export function subscribeWebPushOpen(handler: (open: PushOpen) => void): () => void {
  clickPush ??= new PushModule({
    userId: '',
    sdkApiUrl: SDK_API_URL,
    getAppToken: phoenixToken,
    deviceId: getDeviceId(),
    firebase,
    vapidKey,
  })
  return clickPush.onOpened(handler)
}

// ─── REST-only SDK (contacts identity + call history — no socket needed) ─────

let restSdk: InstavcSdk | null = null

function restInstance(userId: string): InstavcSdk {
  if (restSdk) return restSdk
  // Constructing InstavcSdk does NOT open a socket (that happens in init()); the
  // contacts + calls-history modules are REST-only, so this stays a lightweight,
  // socket-free path.
  restSdk = new InstavcSdk({
    userId,
    sdkApiUrl: SDK_API_URL,
    getAppToken: phoenixToken,
    deviceId: getDeviceId(),
  })
  return restSdk
}

/** Does the current user already have a tenant contact/identity? Used to gate
 *  the chat tab behind the terms-and-conditions screen on first use. */
export async function lookupSelfContact(
  userId: string,
): Promise<{ exists: boolean; contact: SelfContact | null }> {
  return restInstance(userId).contacts.lookupSelf()
}

/** Accept T&C + register (or resolve) the current user's tenant identity. */
export async function registerSelfContact(
  userId: string,
  input: RegisterSelfInput = {},
): Promise<SelfContact> {
  return restInstance(userId).contacts.registerSelf(input)
}

// ─── Block list (REST-only) ──────────────────────────────────────────────────

/**
 * Block a contact — `sdk.contacts.block()`.
 *
 * Enforcement is server-side: their DMs are dropped silently (they can't tell),
 * their calls never ring, and neither party sees the other's presence. 1:1 only
 * — a shared group is unaffected.
 */
export async function blockContact(userId: string, targetId: string): Promise<void> {
  return restInstance(userId).contacts.block(targetId)
}

/** Unblock a contact. Does NOT deliver anything sent while blocked — it was
 *  never stored. */
export async function unblockContact(userId: string, targetId: string): Promise<void> {
  return restInstance(userId).contacts.unblock(targetId)
}

/** Account-level chat preferences (theme + default background), synced across
 *  the user's devices. */
export async function getChatPreferences(userId: string): Promise<ChatPreferences> {
  return restInstance(userId).contacts.getPreferences()
}

/** Update theme and/or default background; returns what is now in force. */
export async function setChatPreferences(
  userId: string,
  patch: Partial<ChatPreferences>,
): Promise<ChatPreferences> {
  return restInstance(userId).contacts.setPreferences(patch)
}

/** Set (or clear) MY background for one conversation. */
export async function setConversationBackground(
  userId: string,
  conversationId: string,
  background: string | null,
): Promise<string | null> {
  return restInstance(userId).conversations.setBackground(conversationId, background)
}

// ─── Starred messages (REST-only) ────────────────────────────────────────────

/** Star a message — private to this user, synced across their devices. */
export async function starMessage(userId: string, messageId: string): Promise<void> {
  return restInstance(userId).stars.star(messageId)
}

/** Remove a star. */
export async function unstarMessage(userId: string, messageId: string): Promise<void> {
  return restInstance(userId).stars.unstar(messageId)
}

/** My starred messages, newest-starred first. */
export async function listStarredMessages(
  userId: string,
  opts: { limit?: number; before?: string } = {},
): Promise<StarredPage> {
  return restInstance(userId).stars.list(opts)
}

/** Which of these message ids have I starred? Drives the per-bubble star icon. */
export async function starredMessageIdsIn(
  userId: string,
  messageIds: string[],
): Promise<string[]> {
  return restInstance(userId).stars.starredIn(messageIds)
}

/** The current user's chat privacy toggles — `sdk.contacts.getPrivacy()`. */
export async function getChatPrivacy(userId: string): Promise<PrivacySettings> {
  return restInstance(userId).contacts.getPrivacy()
}

/** Update a privacy toggle; returns the settings now in force. */
export async function setChatPrivacy(
  userId: string,
  patch: Partial<PrivacySettings>,
): Promise<PrivacySettings> {
  return restInstance(userId).contacts.setPrivacy(patch)
}

/** Everyone the current user has blocked, newest first. */
export async function listBlockedContacts(userId: string): Promise<BlockedContact[]> {
  return restInstance(userId).contacts.listBlocked()
}

/**
 * Past calls for the Calls tab, newest first — `sdk.calls.history()`.
 *
 * Records are written server-authoritatively by signaling when a call ends, so
 * this feed is complete even for calls this browser never saw. Cursor-paged:
 * pass the previous page's `nextCursor` as `before` to load older rows.
 */
export async function fetchCallHistory(
  userId: string,
  opts: { limit?: number; before?: string } = {},
): Promise<CallHistoryPage> {
  return restInstance(userId).calls.history(opts)
}

// ─── Broadcast lists (REST-only) ─────────────────────────────────────────────

/**
 * Broadcast lists — `sdk.broadcasts`. Owner-private one-to-many lists: a send
 * fans out server-side into each recipient's DM with the owner, so recipients
 * see an ordinary private message and reply privately. All REST, no socket.
 */
export async function listBroadcasts(userId: string): Promise<SdkBroadcast[]> {
  return restInstance(userId).broadcasts.list()
}

export async function createBroadcast(
  userId: string,
  input: { name: string; recipientIds: string[]; avatarUrl?: string | null },
): Promise<SdkBroadcast> {
  return restInstance(userId).broadcasts.create(input)
}

export async function updateBroadcast(
  userId: string,
  broadcastId: string,
  input: { name?: string; avatarUrl?: string | null },
): Promise<SdkBroadcast> {
  return restInstance(userId).broadcasts.patch(broadcastId, input)
}

export async function addBroadcastRecipients(
  userId: string,
  broadcastId: string,
  recipientIds: string[],
): Promise<BroadcastRecipient[]> {
  return restInstance(userId).broadcasts.addRecipients(broadcastId, recipientIds)
}

export async function removeBroadcastRecipient(
  userId: string,
  broadcastId: string,
  recipientId: string,
): Promise<BroadcastRecipient[]> {
  return restInstance(userId).broadcasts.removeRecipient(broadcastId, recipientId)
}

export async function deleteBroadcast(userId: string, broadcastId: string): Promise<void> {
  return restInstance(userId).broadcasts.delete(broadcastId)
}

/**
 * Aggregate receipts for a batch of broadcast messages — the ticks in the list
 * thread. A list thread has one participant (you), so the normal cursor-based
 * tick can't work; these aggregate each recipient's cursor on their own DM.
 */
export async function fetchBroadcastReceipts(
  userId: string,
  broadcastId: string,
  messageIds: string[],
): Promise<BroadcastMessageReceipts[]> {
  return restInstance(userId).broadcasts.receipts(broadcastId, messageIds)
}

/** Per-recipient receipts for ONE message — the Message-info sheet. */
export async function fetchBroadcastReceipt(
  userId: string,
  broadcastId: string,
  messageId: string,
): Promise<BroadcastMessageReceipts> {
  return restInstance(userId).broadcasts.receiptFor(broadcastId, messageId)
}

/** WhatsApp's all-or-nothing tick rule applied to a message's receipts. */
export function broadcastStatusFor(
  userId: string,
  receipts: BroadcastMessageReceipts | undefined,
): MessageStatus {
  return restInstance(userId).broadcasts.statusFor(receipts)
}

/** Send to every recipient on the list. Check `failed` for unreachable ones. */
export async function sendBroadcast(
  userId: string,
  broadcastId: string,
  body: string,
): Promise<BroadcastSendResult> {
  return restInstance(userId).broadcasts.send(broadcastId, { body })
}

// ─── Full SDK (chat + presence over the shared socket) ───────────────────────

let fullSdk: InstavcSdk | null = null
let fullSdkUserId: string | null = null

/**
 * Build (once) a full SDK instance bound to the app's shared Phoenix socket,
 * exposing `sdk.chat` + `sdk.presence`. `init()` is a no-op for the socket
 * (it's injected + app-managed) but starts presence on the owner topic.
 */
export async function getInstavcSdk(userId: string, userName?: string): Promise<InstavcSdk> {
  if (fullSdk && fullSdkUserId === userId) return fullSdk
  if (fullSdk && fullSdkUserId !== userId) {
    fullSdk.destroy()
    fullSdk = null
  }
  const socket = await getPhoenixSocket(userId, userName)
  const sdk = new InstavcSdk({
    socket,
    userId,
    userName,
    sdkApiUrl: SDK_API_URL,
    getAppToken: authToken,
    deviceId: getDeviceId(),
    firebase,
    vapidKey,
  })
  await sdk.init({ registerPush: false }) // push handled by register/unregister above
  fullSdk = sdk
  fullSdkUserId = userId
  return sdk
}

/** Tear down the full SDK instance (leaves channels; injected socket untouched). */
export function destroyInstavcSdk(): void {
  fullSdk?.destroy()
  fullSdk = null
  fullSdkUserId = null
}
